/**
 * stageEditorOps — the pure decision logic behind the pipeline Stage editor
 * (SE-5488: rename, reorder, add, remove + protected/terminal flag editing).
 *
 * Every branch the editor takes is expressed here as a pure function so the
 * guard flows are unit-testable in isolation and carry the ticket's Validation
 * Journey EVIDENCE markers, independent of any React/Apollo wiring:
 *
 * - AC1 (rename preserves protection): {@link buildStageRenameInput} emits ONLY
 *   `{ name }`. A rename can never carry `isProtected`/`isTerminal`, so a
 *   protected stage stays protected by construction — the move-gate
 *   (`stageMoveGate`) keeps firing the reason prompt after a rename.
 * - AC2 (delete a rule-referenced stage warns + pauses): {@link resolveStageDeletion}
 *   returns `warn-rules` naming every rule that references the stage (via
 *   trigger/target/condition), which the caller pauses via
 *   `SetPipelineAutomationRuleEnabled` before deleting.
 * - AC3 (a stage with cards cannot be deleted): {@link resolveStageDeletion}
 *   returns `blocked-has-cards` while the stage holds ≥1 card — deletion is
 *   blocked until the stage is empty.
 * - AC4 (reorder persists canonical order): {@link reorderStages} feeds
 *   `computePhasePositions` (see `./phasePositions`, the scheme shared with the
 *   board's drag-and-drop path) to produce the `PhasePosition[]` that
 *   `UpdateKanbanPhasePositions` persists, so the board re-renders in the saved
 *   order.
 * @module features/player-kanban/utils/stageEditorOps
 */

import type {
  PipelineAutomationRuleFragmentFragment,
  UpdateKanbanPhaseInput,
} from "@/generated/graphql";

/**
 * Gap left above the current maximum position when APPENDING a new stage.
 * Appending above the maximum can never collide with the unique
 * `(kanbanBoardId, position)` constraint; reordering rebases the whole board
 * instead — see `./phasePositions`.
 */
export const STAGE_POSITION_GAP = 100000;

/**
 * Resolve the `position` for a stage APPENDED to the end of a board.
 *
 * SE-6558 — this is the ONE APPEND scheme for the feature. Both paths that add
 * a stage to a board that already has stages route through it: the Stages
 * editor panel (`useStageEditor.addStage`) and the board's "New Column" button
 * (`useKanbanPhaseOperations.addPhase`). A caller that instead derives a
 * position from the stage COUNT (`phases.length` → 3, 4, 5…) lands far BELOW
 * the band live stages occupy — real boards sit at 100000/200000/300000, and
 * any board touched by `./phasePositions` sits in `[1M, 1B)` — so the new stage
 * sorts to the FRONT of the ascending-position render order instead of the end.
 * Anchoring strictly above the current maximum is also what keeps the append
 * clear of the unique `(kanbanBoardId, position)` constraint on `KanbanPhase`.
 *
 * Deliberately NOT routed through this: the legacy empty-board seed in
 * `hooks/useKanbanBoard.ts`, which writes `DEFAULT_PHASE_NAMES` at `0..n-1`.
 * It is safe only because it is gated on `phases.length === 0` (and on
 * non-PIPELINE boards), so it never appends onto an occupied board — a later
 * append over `[0..n-1]` still lands above them. Any NEW stage-creation path
 * that can run on a non-empty board belongs here.
 *
 * The seed of `0` matters: it makes the first stage on an empty board land at
 * `STAGE_POSITION_GAP` rather than at `-Infinity` (which `Math.max(...[])`
 * would yield).
 * @param currentPositions - Every position the board currently holds.
 * @returns A position strictly above the board's current maximum.
 */
export const nextAppendedStagePosition = (
  currentPositions: readonly number[]
): number =>
  currentPositions.reduce((max, position) => Math.max(max, position), 0) +
  STAGE_POSITION_GAP;

/** Minimal stage shape the editor logic needs (id + position + flags + name). */
export interface EditableStage {
  readonly id: string;
  readonly name: string;
  readonly position: number;
  readonly isProtected: boolean;
  readonly isTerminal: boolean;
  /** SE-5497: configured time-in-stage target in days (null/0 = no target). */
  readonly timeTargetDays: number | null;
}

/** Minimal card shape: only the owning phase id is needed to count occupancy. */
export interface StageCardRef {
  readonly kanbanPhaseId?: string | null;
}

/**
 * Build the mutation input for a stage rename.
 *
 * Deliberately returns ONLY `{ name }` — never a protection or terminal field —
 * so `UpdateKanbanPhase` leaves `isProtected`/`isTerminal` untouched and the
 * stage keeps triggering the sensitive-move reason prompt after the rename
 * (AC1). This is the single place a rename input is constructed.
 * @param name - The new stage name.
 * @returns An `UpdateKanbanPhaseInput` carrying only the name.
 */
export const buildStageRenameInput = (
  name: string
): UpdateKanbanPhaseInput => ({
  name,
});

/**
 * Sort stages by their canonical `position` ascending, without mutating input.
 * @param stages - The stages to order.
 * @returns A new array ordered by position.
 */
export const sortStagesByPosition = <T extends { readonly position: number }>(
  stages: readonly T[]
): T[] => [...stages].sort((a, b) => a.position - b.position);

/**
 * Move one stage to a target index, producing the new visual order.
 *
 * The input is first normalised by canonical position so the result is
 * independent of the array's incoming order; the moved stage is spliced out and
 * re-inserted at `targetIndex` (clamped to the valid range). This is the pure
 * core of AC4: dragging C before A in `[A, B, C]` (targetIndex 0) yields
 * `[C, A, B]`.
 * @param stages - The current stages (any order).
 * @param movedStageId - The stage being moved.
 * @param targetIndex - The destination index in the position-sorted list.
 * @returns The reordered stages, or the position-sorted input when the id is
 *   unknown.
 */
export const reorderStages = <
  T extends { readonly id: string; readonly position: number },
>(
  stages: readonly T[],
  movedStageId: string,
  targetIndex: number
): T[] => {
  const ordered = sortStagesByPosition(stages);
  const fromIndex = ordered.findIndex(stage => stage.id === movedStageId);
  if (fromIndex === -1) return ordered;

  const clamped = Math.max(0, Math.min(targetIndex, ordered.length - 1));
  const moved = ordered[fromIndex];
  const withoutMoved = ordered.filter((_, index) => index !== fromIndex);
  return [
    ...withoutMoved.slice(0, clamped),
    moved,
    ...withoutMoved.slice(clamped),
  ];
};

/**
 * Shift one stage by a single step in the given direction, producing the new
 * order. Backs the editor's up/down reorder affordance (a cross-platform
 * alternative to a web-only drag surface) over the same primitive as
 * {@link reorderStages}.
 * @param stages - The current stages (any order).
 * @param movedStageId - The stage being nudged.
 * @param direction - `"up"` moves toward index 0; `"down"` toward the end.
 * @returns The reordered stages.
 */
export const nudgeStage = <
  T extends { readonly id: string; readonly position: number },
>(
  stages: readonly T[],
  movedStageId: string,
  direction: "up" | "down"
): T[] => {
  const ordered = sortStagesByPosition(stages);
  const fromIndex = ordered.findIndex(stage => stage.id === movedStageId);
  if (fromIndex === -1) return ordered;
  const targetIndex = direction === "up" ? fromIndex - 1 : fromIndex + 1;
  return reorderStages(ordered, movedStageId, targetIndex);
};

/**
 * Count the cards currently sitting in a stage (AC3 occupancy check).
 * @param cards - The board's cards (only `kanbanPhaseId` is read).
 * @param stageId - The stage to count.
 * @returns The number of cards whose `kanbanPhaseId` matches.
 */
export const countCardsInStage = (
  cards: readonly StageCardRef[],
  stageId: string
): number => cards.filter(card => card.kanbanPhaseId === stageId).length;

/**
 * Find every automation rule that references a stage.
 *
 * A rule references a stage when the stage is its trigger stage, its target
 * (action) stage, or the subject of ANY of its conditions. These are exactly
 * the rules whose behaviour a stage deletion would break, so they are the rules
 * the warning names and the caller pauses before deleting (AC2). Order is
 * preserved from the input.
 * @param rules - The board's automation rules.
 * @param stageId - The stage being deleted.
 * @returns The referencing rules, in input order.
 */
export const findRulesReferencingStage = (
  rules: readonly PipelineAutomationRuleFragmentFragment[],
  stageId: string
): PipelineAutomationRuleFragmentFragment[] =>
  rules.filter(
    rule =>
      rule.triggerKanbanPhaseId === stageId ||
      rule.targetKanbanPhaseId === stageId ||
      rule.conditions.some(condition => condition.kanbanPhaseId === stageId)
  );

/** Deletion is blocked because the stage still holds cards (AC3). */
export interface StageDeletionBlockedHasCards {
  readonly status: "blocked-has-cards";
  /** How many cards must be moved out first. */
  readonly cardCount: number;
}

/**
 * Deletion cannot be confirmed because the card set is not fully loaded, so an
 * apparently-empty stage cannot be *proven* empty. Fail closed (SE-5488 review):
 * a stage whose occupancy is unknown is never deletable until the count is
 * authoritative.
 */
export interface StageDeletionBlockedUnknownOccupancy {
  readonly status: "blocked-unknown-occupancy";
}

/** Deletion needs confirmation because rules reference the stage (AC2). */
export interface StageDeletionWarnRules {
  readonly status: "warn-rules";
  /** The referencing rules, named in the warning and paused on confirm. */
  readonly rules: readonly PipelineAutomationRuleFragmentFragment[];
  /**
   * The rule set was not fully loaded when the plan was resolved, so the named
   * list may be incomplete — the warning degrades to a generic caveat rather
   * than asserting completeness (SE-5488 review). Omitted (falsy) when the rule
   * set is fully loaded.
   */
  readonly mayHaveMoreRules?: boolean;
}

/** Deletion may proceed with no extra warning. */
export interface StageDeletionReady {
  readonly status: "ready";
}

/** The resolved plan for deleting a stage. */
export type StageDeletionPlan =
  | StageDeletionBlockedHasCards
  | StageDeletionBlockedUnknownOccupancy
  | StageDeletionWarnRules
  | StageDeletionReady;

/** Inputs to {@link resolveStageDeletion}. */
export interface ResolveStageDeletionArgs {
  /** The stage being deleted. */
  readonly stage: EditableStage;
  /** The board's cards (for the occupancy guard). */
  readonly cards: readonly StageCardRef[];
  /** The board's automation rules (for the rule-impact warning). */
  readonly rules: readonly PipelineAutomationRuleFragmentFragment[];
  /**
   * Whether the card set is fully loaded (no further pages). When false, an
   * apparently-empty stage cannot be proven empty and the plan fails closed.
   * Defaults to `true` so existing pure callers keep their behaviour.
   */
  readonly cardsComplete?: boolean;
  /**
   * Whether the rule set is fully loaded (no further pages). When false, the
   * referencing-rule list may be incomplete and the warning degrades to a
   * generic caveat. Defaults to `true`.
   */
  readonly rulesComplete?: boolean;
}

/**
 * Resolve what deleting a stage requires, as a single discriminated plan.
 *
 * Guard precedence is deliberate:
 * 1. `blocked-has-cards` — a non-empty stage can NEVER be deleted directly
 *    (AC3); the cards must be moved out first. This wins even when rules also
 *    reference the stage, because the block is absolute.
 * 2. `blocked-unknown-occupancy` — an apparently-empty stage whose card set is
 *    still loading cannot be proven empty, so deletion fails closed until the
 *    occupancy count is authoritative (SE-5488 review).
 * 3. `warn-rules` — an empty stage that rules reference needs a confirmation
 *    naming those rules; on confirm the caller pauses each rule, then deletes
 *    (AC2). When the rule set is still loading, this also covers the case where
 *    NO referencing rule has been seen yet — we cannot assert the stage is
 *    unreferenced, so we warn generically (`mayHaveMoreRules`) rather than
 *    resolve to `ready`.
 * 4. `ready` — an empty, unreferenced stage (both sets fully loaded) deletes
 *    with no extra step.
 * @param args - {@link ResolveStageDeletionArgs}.
 * @param args.stage - The stage being deleted.
 * @param args.cards - The board's cards (occupancy guard).
 * @param args.rules - The board's automation rules (rule-impact warning).
 * @param args.cardsComplete - Whether the card set is fully loaded.
 * @param args.rulesComplete - Whether the rule set is fully loaded.
 * @returns The {@link StageDeletionPlan}.
 */
export const resolveStageDeletion = ({
  stage,
  cards,
  rules,
  cardsComplete = true,
  rulesComplete = true,
}: ResolveStageDeletionArgs): StageDeletionPlan => {
  const cardCount = countCardsInStage(cards, stage.id);
  if (cardCount > 0) {
    return { status: "blocked-has-cards", cardCount };
  }
  if (!cardsComplete) {
    return { status: "blocked-unknown-occupancy" };
  }

  const referencingRules = findRulesReferencingStage(rules, stage.id);
  if (referencingRules.length > 0) {
    return rulesComplete
      ? { status: "warn-rules", rules: referencingRules }
      : {
          status: "warn-rules",
          rules: referencingRules,
          mayHaveMoreRules: true,
        };
  }
  if (!rulesComplete) {
    // No referencing rule seen, but the rule set is still paginating — we cannot
    // assert the stage is unreferenced, so warn generically instead of ready.
    return { status: "warn-rules", rules: [], mayHaveMoreRules: true };
  }

  return { status: "ready" };
};

/** Inputs to {@link buildStageProtectionNotice}. */
export interface StageProtectionNoticeArgs {
  /** The stage's display name. */
  readonly name: string;
  /** Whether the stage is flagged sensitive (protected). */
  readonly isProtected: boolean;
  /** Whether the stage is flagged terminal. */
  readonly isTerminal: boolean;
  /** How many cards currently sit in the stage. */
  readonly cardCount: number;
  /** How many automation rules reference the stage. */
  readonly ruleCount: number;
}

/**
 * Build the inline protection notice shown under a flagged stage's row.
 *
 * It states what the flags actually COST before the user touches them, using
 * the same two numbers the delete guard uses: the occupancy that blocks a
 * delete ({@link countCardsInStage}) and the rules a delete or de-flag would
 * pause ({@link findRulesReferencingStage}). An unflagged stage, or a flagged
 * stage with neither cards nor referencing rules, has nothing to warn about and
 * returns `""` so the caller renders no notice at all.
 * @param args - {@link StageProtectionNoticeArgs}.
 * @param args.name - The stage's display name.
 * @param args.isProtected - Whether the stage is flagged sensitive.
 * @param args.isTerminal - Whether the stage is flagged terminal.
 * @param args.cardCount - Cards currently in the stage.
 * @param args.ruleCount - Rules referencing the stage.
 * @returns The notice sentence, or `""` when there is nothing to say.
 */
export const buildStageProtectionNotice = ({
  name,
  isProtected,
  isTerminal,
  cardCount,
  ruleCount,
}: StageProtectionNoticeArgs): string => {
  if (!isProtected && !isTerminal) return "";
  if (cardCount === 0 && ruleCount === 0) return "";

  const kind = isProtected ? "sensitive" : "terminal";
  const cards = cardCount === 1 ? "card" : "cards";
  const rules = ruleCount === 1 ? "rule" : "rules";

  const lead = `“${name}” is a ${kind} stage with ${cardCount} ${cards}.`;
  const move = cardCount > 0 ? " To delete it, move the cards first." : "";
  const paused =
    ruleCount > 0
      ? ` Deleting or de-flagging it pauses ${ruleCount} ${rules} that reference it.`
      : "";

  return `${lead}${move}${paused} Everything is logged.`;
};

/**
 * Whether an open Stage editor is bound to a board that is no longer the active
 * one — i.e. the board was switched while the editor stayed open.
 *
 * The editor's open state lives in a module-level reactive var, so a plain
 * key-remount cannot reset it; instead the container renders the editor closed
 * (and skips its queries/mutations) whenever this returns true, so a stale
 * editor can never operate on the previously-selected board (SE-5488 review).
 * @param open - Whether the editor reactive var reports open.
 * @param targetBoardId - The board the editor was opened against (var state).
 * @param activeBoardId - The board the screen is currently showing.
 * @returns True when the editor is open but bound to a non-active board.
 */
export const isStaleStageEditorTarget = (
  open: boolean,
  targetBoardId: string | null,
  activeBoardId: string | null | undefined
): boolean =>
  open &&
  targetBoardId != null &&
  targetBoardId !== "" &&
  activeBoardId != null &&
  targetBoardId !== activeBoardId;
